// Package imports:
import React, { useMemo, useState } from 'react';
import cx from 'classnames';
// Component imports:
import Alert from '../../ui-elements/Alert/Alert';
import Loading from '../../ui-elements/Loading/Loading';
import DelayOrClosedLabel from '../DelayOrClosedLabel/DelayOrClosedLabel';
import DisplayBox from '../DisplayBox/DisplayBox';
// Service imports:
import { formatNumber, convertToPercentage } from '../../services/utils';
import { useApiLmdData } from '../../services/apiHooks';
// Type imports:
import { IApiLmdStatistics } from '../../types/HlutabrefTypes';
import { IApiLmdSnapshot } from '../../types/SkuldabrefTypes';
import { IDefaultProps } from '../../types/Types';

type TimePeriod = 'intraday' | 'weeks4' | 'months3' | 'year';

type HeatMapColor = 'pos' | 'neg' | 'neutral';
interface IHeatMapData {
    symbol: string | null,
    color: HeatMapColor,
    value: number | null,
    changeValue: number | null
}

type Props = IDefaultProps & React.HTMLAttributes<HTMLDivElement>;

const HeatMap: React.FC<Props> = ({
    refreshRateMs,
    accessToken,
    ...props
}) => {
    const [statistics] = useApiLmdData<IApiLmdStatistics[]>(
        '/v1/market_data/v1/keldan/exchanges/[XICE;FNIS]/statistics',
        accessToken,
        refreshRateMs
    );
    const [timePeriod, setTimePeriod] = useState<TimePeriod>('intraday');
    const [sharesData] = useApiLmdData<IApiLmdSnapshot[]>(
        '/v1/market_data/v1/keldan/exchanges/[XICE;FNIS]/equities/delayed_snapshot',
        accessToken,
        refreshRateMs
    );

    const displayData = useMemo(() => {
        const newData: IHeatMapData[] = [];
        if (statistics.data === null || sharesData.data === null) return [];
        if (timePeriod === 'intraday') {
            for (let entry of sharesData.data) {
                const changeVal = entry.intraday_per_change ?? 0;
                newData.push({
                    symbol: entry.symbol,
                    color: changeVal === null ? 'neutral' : changeVal > 0 ? 'pos' : changeVal < 0 ? 'neg' : 'neutral',
                    value: entry.last_price,
                    changeValue: changeVal
                })
            }
        } else {
            for (let stat of statistics.data.filter(s => s.security_type === 'share')) {
                const dataEntry = sharesData.data.find(e => e.symbol === stat.symbol);
                const changeVal = ((dataEntry?.last_price ?? 0) - (stat[`${timePeriod}_price`] ?? 0))/(stat[`${timePeriod}_price`] ?? 1);
                newData.push({
                    symbol: stat.symbol,
                    color: changeVal === null ? 'neutral' : changeVal > 0 ? 'pos' : changeVal < 0 ? 'neg' : 'neutral',
                    value: dataEntry?.last_price ?? null,
                    changeValue: changeVal
                });
            }
        }
        newData.sort((a, b) => {
            const aval = a.changeValue;
            const bval = b.changeValue;
            return aval === null && bval === null
                ? 0
                : aval === null
                ? 1
                : bval === null
                ? -1
                : aval > bval
                ? -1
                : aval < bval
                ? 1
                : 0;
        });
        return newData;
    }, [statistics, sharesData, timePeriod]);
    const dataLength = displayData.length;

    return (
        <DisplayBox className='KCL_heat-map'
            title="Hitakort"
            statusComponent={<DelayOrClosedLabel closeType='shares' />}
            asideComponent={
                <div className="time-period-buttons">
                    <button
                        className={cx({'is-active': timePeriod === 'intraday'})}
                        onClick={() => setTimePeriod('intraday')}
                    >
                        Í dag
                    </button>
                    <button 
                        className={cx({'is-active': timePeriod === 'weeks4'})}
                        onClick={() => setTimePeriod('weeks4')}
                    >
                        1 mán.
                    </button>
                    <button
                        className={cx({'is-active': timePeriod === 'months3'})}
                        onClick={() => setTimePeriod('months3')}
                    >
                        3 mán.
                    </button>
                    <button
                        className={cx({'is-active': timePeriod === 'year'})}
                        onClick={() => setTimePeriod('year')}
                    >
                        Þetta ár
                    </button>
                </div>
            }
            {...props}
        >
            {(statistics instanceof Error)
            ? <Alert type='error' headText={statistics.message}/>
            : (statistics === null)
            ? <Loading />
            : (
                <div className="heat-map-body">
                    <div className='heat-map-container'>
                        {displayData.map((datum, i) => {
                            return <a key={i}
                                href={`/Markadir/Hlutabref/${datum.symbol}`}
                                className={cx(
                                    "heat-map-item",
                                    datum.color,
                                    i === 0 ? 'first' : i === dataLength - 1 ? 'last' : ''
                                )}
                            >
                                <span className="symbol">{datum.symbol}</span>
                                <span className="values">
                                    {formatNumber(datum.value, null, 2)} ({convertToPercentage(datum.changeValue, true)})
                                </span>
                            </a>
                        })}
                    </div>
                </div>
            )}
        </DisplayBox>
    );
}

export default HeatMap;